import { Controller, useFormState } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import Form from 'dashboard/layouts/FormLayout/Form';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { ApiKey } from 'dashboard/models/api-key';
import Button from 'design-system/components/Button';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label, Paragraph } from 'design-system/components/Text';
import { useFormSecurely } from 'lib/security/reactHookForm';

interface FormExpireApiKeyProps {
  apiKey?: ApiKey;
  defaultValues?: FormExpireApiKeyData;
  isOpen: boolean;
  handleSubmit: (apiKey: ApiKey, data: FormExpireApiKeyData) => void;
  handleCancel?: () => void;
  handleCloseModal: () => void;
}

export const FormExpireApiKey = ({
  apiKey,
  defaultValues,
  isOpen,
  handleSubmit,
  handleCancel = () => null,
  handleCloseModal,
}: FormExpireApiKeyProps) => {
  const {
    handleSubmit: handleReactHookFormSubmit,
    control,
    reset,
  } = useFormSecurely<FormExpireApiKeyData>({
    mode: 'onChange',
    defaultValues,
  });

  const { isSubmitting, isDirty, errors } = useFormState({
    control,
  });
  const disabled = isSubmitting;

  const closeModal = () => {
    handleCloseModal();
    reset();
  };

  const submit = handleReactHookFormSubmit((data: FormExpireApiKeyData) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    handleSubmit(apiKey, data);
    closeModal();
  });

  const cancel = () => {
    handleCancel();
    closeModal();
  };

  return (
    <ModalWrapper isOpen={isOpen} handleCloseModal={closeModal}>
      <Form
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
      >
        <ModalContainer data-test='form--expire-api-key__modal'>
          <ModalHeader data-test='form--expire-api-key__modal__header' onClose={closeModal}>
            Expire API key
          </ModalHeader>

          <ModalBody>
            <Paragraph color='primary'>
              You will be unable to perform any actions with the key once it has expired.
            </Paragraph>

            <Spacer height={2} />

            <Label
              htmlFor='form--expire-api-key__select--expires-in-seconds'
              size='lg'
              weight='bold'
            >
              Time until expiration
            </Label>
            <Spacer height={1} />
            <Controller
              name='expires-in-seconds'
              control={control}
              rules={{ required: 'Please select an expiration time.' }}
              render={({ field }) => (
                <Select
                  {...field}
                  defaultValue=''
                  id='form--expire-api-key__select--expires-in-seconds'
                  data-test='form--expire-api-key__select--expires-in-seconds'
                  placeholder='Select an expiration...'
                  portal={false}
                >
                  <Option value='0'>Immediately</Option>
                  <Option value='3600'>In 1 hour</Option>
                  <Option value='86400'>In 24 hours</Option>
                  <Option value='259200'>In 3 days</Option>
                  <Option value='604800'>In 7 days</Option>
                </Select>
              )}
            />

            {errors['expires-in-seconds'] && (
              <FormError>{errors['expires-in-seconds'].message}</FormError>
            )}

            <Spacer height={4} />

            <HStack equal={true}>
              <Button
                type='button'
                variant='basic'
                onClick={cancel}
                id='form--expire-api-key__button--cancel'
                data-test='form--expire-api-key__button--cancel'
              >
                Cancel
              </Button>
              <Button
                type='button'
                variant='danger'
                disabled={disabled || !isDirty}
                onClick={submit}
                data-test='form--expire-api-key__button--submit'
                id='form--expire-api-key__button--submit'
              >
                Submit
              </Button>
            </HStack>
          </ModalBody>
        </ModalContainer>
      </Form>
    </ModalWrapper>
  );
};

export interface FormExpireApiKeyData {
  'expires-in-seconds': string;
}
